/* prettier-ignore-file */
import React, { useEffect, useState } from "react";
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from "react-native";
import { useNavigate, useLocation } from "react-router-dom";
import { functions } from "../firebase.config";
import { httpsCallable } from "firebase/functions";
import { useBookings, useNewBookingStore, useSettings, useStripeTestMode } from "../stores";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase.config";
import { getCollectionPath } from "../utils/dbPaths";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bookingsFromStore = useBookings(state => state.bookings);
  const settings = useSettings((state) => state.settings);
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);
  
  // Parse URL parameters from search string
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get("sid");
  const bookingIdParam = searchParams.get("bookingId");
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("Processing payment...");
  const [error, setError] = useState(null);

  useEffect(() => {
    const processPayment = async () => {
      try {
        setLoading(true);
        setStatus("Verifying payment...");

        // CRITICAL: Verify that this page was accessed from Stripe's payment success flow
        if (!sessionId) {
          throw new Error("Invalid access: No payment session ID found. This page must be accessed from Stripe checkout.");
        }

        // Verify the Stripe session
        setStatus("Verifying Stripe payment session...");
        try {
          const verifyStripeSession = httpsCallable(functions, "verifyStripeSession");
          const verificationResult = await verifyStripeSession({ sessionId, testMode: useTestStripe });
          
          if (!verificationResult.data?.verified) {
            throw new Error("Payment session verification failed");
          }

          if (verificationResult.data.paymentStatus !== "paid") {
            throw new Error(`Payment not completed. Status: ${verificationResult.data.paymentStatus}`);
          }

          // Payment verified - clear the new booking store immediately
          console.log("✅ Payment verified - clearing new booking store");
          const { clearBooking } = useNewBookingStore.getState();
          clearBooking();
          localStorage.removeItem("latestBooking");
          localStorage.removeItem("latestBookingId");
          console.log("✅ New booking store cleared");

        } catch (verifyError) {
          console.error("❌ Stripe session verification failed:", verifyError);
          throw new Error(`Payment verification failed: ${verifyError.message || "Invalid payment session"}`);
        }

        // Wait for booking to appear in store (processed by webhook)
        setStatus("Finalizing booking...");
        
        // We rely on the webhook to move the booking from pending to confirmed.
        // We rely on the store listener to update our local cache.
        // We just need to wait until it appears.
        
        let tempBookingId = bookingIdParam || localStorage.getItem("latestBookingId");
        
        if (!tempBookingId) {
           // If we don't have ID, we can't find it easily without querying by session (which requires DB call).
           // But verifyStripeSession might return metadata?
           // For now, assume ID is passed or in local storage.
           // If not, we might be stuck.
           // But the user flow guarantees bookingIdParam usually.
        }

        if (tempBookingId) {
            // Check if already in store
            const found = bookingsFromStore.find(b => b.id === tempBookingId || b.bookingId === tempBookingId);
            
            if (found) {
                // Success! Clear pending booking from Firestore
                try {
                  const pendingBookingsPath = getCollectionPath("pendingBookings");
                  const pendingBookingRef = doc(db, pendingBookingsPath, tempBookingId);
                  await deleteDoc(pendingBookingRef);
                  console.log("✅ Pending booking cleared from Firestore");
                } catch (deleteError) {
                  console.error("❌ Error clearing pending booking:", deleteError);
                  // Continue anyway - booking is already confirmed
                }
                
                // Store already cleared after payment verification, but ensure it's cleared again
                // (in case this code path runs before the verification clears it)
                const { clearBooking } = useNewBookingStore.getState();
                clearBooking();
                localStorage.removeItem("latestBooking");
                localStorage.removeItem("latestBookingId");
                navigate(`/view-booking/${encodeURIComponent(tempBookingId)}`, {
                    state: {
                        paymentSuccess: true,
                        bookingId: tempBookingId,
                    },
                });
                return;
            }
            
            // If not found yet, we rely on the useEffect dependency on bookingsFromStore to retry?
            // Actually, this async function runs once.
            // We should split the logic:
            // 1. Verify session (once).
            // 2. Watch store for booking (continuous).
        } else {
             // Fallback if no ID
             throw new Error("Booking ID missing. Please contact support.");
        }

      } catch (err) {
        console.error("❌ Payment processing error:", err);
        setError(err.message || "Failed to process payment");
        setLoading(false);
      }
    };

    // Only process if we have a session ID
    if (sessionId) {
      processPayment();
    } else {
      setError("Invalid access: No payment session ID found.");
      setLoading(false);
    }
  }, [sessionId]); // Run once on mount (dependency sessionId)

  // Watch for booking appearance in store
  useEffect(() => {
      const clearPendingAndNavigate = async () => {
          const tempBookingId = bookingIdParam || localStorage.getItem("latestBookingId");
          if (tempBookingId && bookingsFromStore.length > 0) {
              const found = bookingsFromStore.find(b => b.id === tempBookingId || b.bookingId === tempBookingId);
              if (found) {
                  // Clear pending booking from Firestore
                  try {
                    const pendingBookingsPath = getCollectionPath("pendingBookings");
                    const pendingBookingRef = doc(db, pendingBookingsPath, tempBookingId);
                    await deleteDoc(pendingBookingRef);
                    console.log("✅ Pending booking cleared from Firestore");
                  } catch (deleteError) {
                    console.error("❌ Error clearing pending booking:", deleteError);
                    // Continue anyway - booking is already confirmed
                  }
                  
                  // Store already cleared after payment verification, but ensure it's cleared again
                  // (in case this code path runs before the verification clears it)
                  const { clearBooking } = useNewBookingStore.getState();
                  clearBooking();
                  localStorage.removeItem("latestBooking");
                  localStorage.removeItem("latestBookingId");
                  navigate(`/view-booking/${encodeURIComponent(tempBookingId)}`, {
                      state: {
                          paymentSuccess: true,
                          bookingId: tempBookingId,
                      },
                  });
              }
          }
      };
      
      clearPendingAndNavigate();
  }, [bookingsFromStore, bookingIdParam, navigate]);

  const handleGoBack = () => {
    navigate("/rental-bookings");
  };

  // Show only loading spinner - navigate immediately when booking is found
  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {error ? (
          <>
            <Text style={styles.errorText}>❌ {error}</Text>
            <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.button} onPress={handleGoBack}>
              <Text style={styles.buttonText}>Go Back</Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.statusText}>{status}</Text>
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  content: {
    alignItems: "center",
    justifyContent: "center",
  },
  successText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#1F8A5B",
    marginBottom: 20,
    textAlign: "center",
  },
  statusText: {
    fontSize: 16,
    color: "#5C6B68",
    marginTop: 20,
    textAlign: "center",
  },
  errorText: {
    fontSize: 16,
    color: "#C4453D",
    marginBottom: 20,
    textAlign: "center",
  },
  button: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    marginTop: 20,
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
});

export default PaymentSuccess;

